import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AU_STATES, INTERESTED_SOLUTIONS, MANAGER_ROLES, fmtDate } from "../lib/constants";
import AddressAutocomplete from "../components/AddressAutocomplete";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { ActionsMenu } from "../components/ActionsMenu";

const empty = { first_name: "", last_name: "", phone: "", email: "", street: "", suburb: "", state: "VIC", postcode: "", source: "Manual", interested_solution: "", assigned_to: "", suppress_emails: false };

const priorityColor = {
  Urgent: "bg-red-100 text-red-700", High: "bg-orange-100 text-orange-700",
  Medium: "bg-blue-100 text-blue-700", Low: "bg-slate-100 text-slate-600",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unassigned: 0, mine: 0 });
  const [sources, setSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [stages, setStages] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("new");
  const [stageFilter, setStageFilter] = useState("");
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState([]);
  const [bulkUser, setBulkUser] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null); // "sms" | "email" | null
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAssign = MANAGER_ROLES.includes(user?.role);

  const load = () => {
    const view = tab === "all" ? "" : `?view=${tab}`;
    api.get(`/leads${view}`).then((r) => setLeads(r.data));
    api.get("/leads/counts").then((r) => setCounts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); setSelected([]); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => {
    api.get("/config/sources").then((r) => setSources(r.data));
    api.get("/config/stages").then((r) => setStages(r.data));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const stageName = (id) => stages.find((s) => s.id === id)?.name || id;
  const userName = (id) => users.find((u) => u.id === id)?.name || (id ? "—" : "Unassigned");

  const [dupWarn, setDupWarn] = useState(null);
  const doCreate = async (force) => {
    try {
      const assigned = form.assigned_to === "__UNASSIGNED__" ? "" : (form.assigned_to || user.id);
      const { data } = await api.post("/leads", { ...form, assigned_to: assigned, force_create: force });
      toast.success(`Lead ${data.uid} created${data.is_duplicate ? " (flagged as duplicate)" : ""}`);
      setShow(false); setForm(empty); setDupWarn(null); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === "duplicate_lead") setDupWarn(d.duplicates || []);
      else toast.error(errMsg(err));
    }
  };
  const create = async (e) => { e.preventDefault(); await doCreate(false); };

  const normPhone = (p) => { let d = String(p || "").replace(/\D/g, ""); if (d.startsWith("61") && d.length >= 11) d = "0" + d.slice(2); return d; };
  const qDigits = normPhone(q);
  const filtered = leads.filter((l) =>
    (!stageFilter || l.stage === stageFilter) &&
    (!q || `${l.uid} ${l.first_name} ${l.last_name} ${l.full_address} ${l.phone} ${l.email} ${l.suburb} ${l.postcode}`.toLowerCase().includes(q.toLowerCase())
    || (qDigits.length >= 4 && normPhone(l.phone).includes(qDigits))));
  const sorted = [...filtered].sort((a, b) => {
    const av = a.created_at || "", bv = b.created_at || "";
    return sortBy === "old" ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = filtered.length > 0 && filtered.every((l) => selected.includes(l.id));
  const toggleAll = () => setSelected(allSelected ? [] : filtered.map((l) => l.id));

  const bulkAssign = async () => {
    try {
      const { data } = await api.post("/leads/bulk/assign", { lead_ids: selected, assigned_to: bulkUser || null });
      toast.success(`${data.updated} lead(s) ${bulkUser ? "assigned" : "unassigned"}`);
      setSelected([]); setBulkUser(""); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const tabs = [
    { key: "all", label: "All Leads", count: counts.total },
    { key: "unassigned", label: "Unassigned", count: counts.unassigned },
    { key: "mine", label: "My Leads", count: counts.mine },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-head font-extrabold tracking-tight">Leads</h1>
          <p className="text-muted-foreground">{counts.total} total · {counts.unassigned} unassigned</p>
        </div>
        <div className="flex items-center gap-2">
          <ActionsMenu id="leads-actions" items={[
            { id: "lead-bulk-upload-btn", icon: "Upload", label: "Bulk upload", hidden: !canAssign, onSelect: () => setBulkOpen(true) },
            { id: "recycle-bin-btn", icon: "Trash2", label: "Recycle Bin", hidden: user?.role !== "super_admin", onSelect: () => navigate("/app/leads/recycle-bin") },
          ]} />
          <button data-testid="new-lead-btn" onClick={() => setShow(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] hover:brightness-110 transition-all">
            <Icons.Plus size={18} /> New Lead
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`leads-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${t.key === "unassigned" && t.count > 0 ? "bg-amber-500 text-white" : "bg-muted text-muted-foreground"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Icons.Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
          <input data-testid="lead-search" placeholder="Search UID, name, phone, email, suburb, postcode…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-input bg-card focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
        </div>
        <div className="flex items-center gap-2">
          <Icons.Filter size={15} className="text-muted-foreground" />
          <select data-testid="lead-stage-filter" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
            className="px-3 py-2.5 rounded-md border border-input bg-card text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none">
            <option value="">All stages</option>
            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Icons.ArrowDownUp size={15} className="text-muted-foreground" />
          <select data-testid="lead-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2.5 rounded-md border border-input bg-card text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none">
            <option value="new">Created — Newest first</option>
            <option value="old">Created — Oldest first</option>
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-primary/5 border border-primary/20 rounded-md p-3" data-testid="bulk-bar">
          <span className="text-sm font-semibold">{selected.length} selected</span>
          <button onClick={() => setBulkAction("email")} data-testid="bulk-email-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:brightness-110 transition-all">
            <Icons.Mail size={15} /> Email
          </button>
          <button onClick={() => setBulkAction("sms")} data-testid="bulk-sms-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-secondary text-secondary-foreground font-semibold text-sm hover:brightness-110 transition-all">
            <Icons.MessageSquare size={15} /> Text
          </button>
          {canAssign && (
            <div className="flex items-center gap-2 pl-3 sm:border-l sm:border-primary/20">
              <select value={bulkUser} onChange={(e) => setBulkUser(e.target.value)} data-testid="bulk-assign-user"
                className="px-3 py-2 rounded-md border border-input bg-background text-sm">
                <option value="">— Unassign —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <button onClick={bulkAssign} data-testid="bulk-assign-btn"
                className="px-4 py-2 rounded-md border border-primary/40 text-primary font-semibold text-sm hover:bg-primary/10">Assign</button>
            </div>
          )}
          <button onClick={() => setSelected([])} data-testid="bulk-clear-btn" className="text-sm text-muted-foreground ml-auto">Clear</button>
        </div>
      )}

      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="leads-cards">
        {filtered.length > 0 && (
          <label className="flex items-center gap-2 text-sm font-medium px-1" data-testid="select-all-mobile">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            Select all ({filtered.length})
          </label>
        )}
        {sorted.map((l) => (
          <div key={l.id} data-testid={`lead-card-${l.uid}`}
            className={`bg-card border rounded-md p-4 shadow-sm active:bg-muted/50 transition-colors ${selected.includes(l.id) ? "border-primary ring-1 ring-primary" : "border-border"}`}>
            <div className="flex items-start gap-3">
              <input type="checkbox" checked={selected.includes(l.id)} onChange={() => toggleSel(l.id)}
                data-testid={`select-mobile-${l.uid}`} className="mt-1 h-4 w-4 shrink-0" onClick={(e) => e.stopPropagation()} />
              <div className="flex-1 min-w-0" onClick={() => navigate(`/app/leads/${l.id}`)}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-primary">
                    {l.is_duplicate && <span data-testid={`dup-dot-${l.uid}`} title="Possible duplicate lead" className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 mr-1.5 align-middle animate-pulse" />}
                    {l.uid}</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-secondary/10 text-secondary">{stageName(l.stage)}</span>
                </div>
                <div className="font-medium mt-1">{l.first_name} {l.last_name}</div>
                <div className="text-sm text-muted-foreground">{l.phone}</div>
                <div className="text-xs text-muted-foreground mt-1 break-words" data-testid={`lead-card-postcode-${l.uid}`}>{[l.suburb, l.postcode].filter(Boolean).join(" ") || "—"}</div>
                {l.interested_solution && <div className="text-xs text-primary mt-0.5">{l.interested_solution}</div>}
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className="px-2 py-0.5 rounded bg-muted">{l.source}</span>
                  <span className={l.assigned_to ? "text-muted-foreground" : "text-amber-600 font-medium"}>{userName(l.assigned_to)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center text-muted-foreground py-8">No leads here</p>}
      </div>

      <div className="hidden md:block bg-card border border-border rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead><tr className="text-left label-caps border-b border-border">
            <th className="p-3 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all" /></th>
            <th className="p-3">UID</th><th>Customer</th><th>Interested In</th><th>Suburb/PC</th><th>Source</th><th>Assigned To</th><th>Stage</th><th>Priority</th><th>Created</th></tr></thead>
          <tbody>
            {sorted.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.uid}`}
                className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.includes(l.id)} onChange={() => toggleSel(l.id)} data-testid={`select-${l.uid}`} /></td>
                <td className="p-3 font-semibold text-primary cursor-pointer" onClick={() => navigate(`/app/leads/${l.id}`)}>
                  {l.is_duplicate && <span data-testid={`dup-dot-${l.uid}`} title="Possible duplicate lead" className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 mr-1.5 align-middle animate-pulse" />}
                  {l.uid}</td>
                <td className="cursor-pointer" onClick={() => navigate(`/app/leads/${l.id}`)}>{l.first_name} {l.last_name}<div className="text-xs text-muted-foreground">{l.phone}</div></td>
                <td className="text-xs">{l.interested_solution || "-"}</td>
                <td className="text-xs text-muted-foreground">{[l.suburb, l.postcode].filter(Boolean).join(" ") || "-"}</td>
                <td><span className="text-xs px-2 py-0.5 rounded bg-muted">{l.source}</span></td>
                <td className="text-xs">{l.assigned_to ? userName(l.assigned_to) : <span className="text-amber-600 font-medium">Unassigned</span>}</td>
                <td><span className="text-xs font-semibold px-2 py-0.5 rounded bg-secondary/10 text-secondary">{stageName(l.stage)}</span></td>
                <td><span className={`text-xs font-semibold px-2 py-0.5 rounded ${priorityColor[l.priority] || priorityColor.Medium}`}>{l.priority || "Medium"}</span></td>
                <td className="text-xs text-muted-foreground">{fmtDate(l.created_at)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">No leads here</td></tr>}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create}
            onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") e.preventDefault(); }}
            className="bg-card rounded-md w-full max-w-lg p-6 shadow-[0_8px_30px_rgb(0,0,0,0.12)] max-h-[90vh] overflow-y-auto" data-testid="lead-form">
            <h2 className="text-xl font-head font-extrabold mb-1">New Lead</h2>
            <p className="text-xs text-muted-foreground mb-4">A unique UID (GSPxxxx) is generated automatically.</p>
            <div className="grid grid-cols-2 gap-3">
              {[["first_name", "First Name*"], ["last_name", "Last Name"], ["phone", "Phone"], ["email", "Email"]].map(([k, l]) => (
                <div key={k}><label className="label-caps">{l}</label>
                  <input required={k === "first_name"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`lead-${k}`} className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              ))}
              <div className="col-span-2"><label className="label-caps">Interested Solution</label>
                <select value={form.interested_solution} onChange={(e) => setForm({ ...form, interested_solution: e.target.value })}
                  data-testid="lead-interested" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  <option value="">— Select —</option>{INTERESTED_SOLUTIONS.map((s) => <option key={s}>{s}</option>)}</select></div>
              <div className="col-span-2"><label className="label-caps">Address (Google autocomplete)</label>
                <AddressAutocomplete value={form.street} testid="lead-street"
                  onManualChange={(v) => setForm((f) => ({ ...f, street: v }))}
                  onSelect={(a) => setForm((f) => ({ ...f, ...a }))} />
                <div className="text-[11px] text-muted-foreground mt-0.5">Start typing to auto-fill suburb, state & postcode.</div></div>
              <div><label className="label-caps">Suburb</label>
                <input value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })}
                  data-testid="lead-suburb" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="label-caps">State</label>
                  <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                    data-testid="lead-state" className="w-full mt-1 px-2 py-2 rounded-md border border-input bg-background">
                    {AU_STATES.map((s) => <option key={s}>{s}</option>)}</select></div>
                <div><label className="label-caps">Postcode</label>
                  <input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                    data-testid="lead-postcode" className="w-full mt-1 px-2 py-2 rounded-md border border-input bg-background" /></div>
              </div>
              <div><label className="label-caps">Source</label>
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                  data-testid="lead-source" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  {sources.map((s) => <option key={s}>{s}</option>)}</select></div>
              <div><label className="label-caps">Assign To</label>
                <select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                  data-testid="lead-assign" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  <option value="">Me</option>
                  {canAssign && <option value="__UNASSIGNED__">Unassigned (send to inbox)</option>}
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></div>
            </div>
            <label className="flex items-start gap-2.5 mt-4 p-3 rounded-md border border-border bg-muted/30 cursor-pointer text-sm">
              <input type="checkbox" checked={!!form.suppress_emails} onChange={(e) => setForm({ ...form, suppress_emails: e.target.checked })}
                data-testid="lead-suppress-emails" className="mt-0.5" />
              <span>
                <span className="font-medium">Don't send automated emails for this lead</span>
                <span className="block text-[11px] text-muted-foreground mt-0.5">Skips the customer thank-you &amp; "now with a specialist" emails. Use this when you'll contact the customer yourself. You can change it later on the lead.</span>
              </span>
            </label>
            <div className="flex gap-2 justify-end mt-6">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 rounded-md border border-border">Cancel</button>
              <button type="submit" data-testid="lead-submit" className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold">Create Lead</button>
            </div>
          </form>
        </div>
      )}

      {dupWarn && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="duplicate-warning">
          <div className="bg-card rounded-md w-full max-w-lg p-6 shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-1">
              <Icons.AlertTriangle size={20} className="text-red-500" />
              <h2 className="text-xl font-head font-extrabold">Possible duplicate lead</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">This customer looks like {dupWarn.length > 1 ? "existing leads" : "an existing lead"}. Please check before creating a new one.</p>
            <div className="space-y-2">
              {dupWarn.map((d) => (
                <div key={d.id} className="border border-red-200 bg-red-50 rounded-md p-3 text-sm" data-testid={`dup-item-${d.uid}`}>
                  <div className="flex items-center justify-between">
                    <button data-testid={`duplicate-open-lead-${d.id}`} className="font-semibold text-primary underline" onClick={() => navigate(`/app/leads/${d.id}`)}>{d.uid} · {d.name}</button>
                    <span className="text-xs px-2 py-0.5 rounded bg-muted">{d.stage || d.source}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Assigned to <b className="text-foreground">{d.assigned_to_name}</b> · matched on <b className="text-red-600">{d.match_on.join(", ")}</b>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{[d.phone, d.email, d.full_address].filter(Boolean).join(" · ")}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setDupWarn(null)} data-testid="dup-cancel" className="px-4 py-2 rounded-md border border-border">Cancel</button>
              <button onClick={() => doCreate(true)} data-testid="dup-force-create" className="px-4 py-2 rounded-md bg-red-500 text-white font-semibold">Create anyway</button>
            </div>
          </div>
        </div>
      )}

      {bulkOpen && <LeadBulkUpload users={users} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
      {bulkAction && (
        <BulkMessageModal channel={bulkAction} count={selected.length} leadIds={selected}
          onClose={() => setBulkAction(null)}
          onSent={() => { setBulkAction(null); setSelected([]); load(); }} />
      )}
    </div>
  );
}

function parseCsv(text) {
  const rows = [];
  let row = [], field = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQ = false;
      else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      if (field !== "" || row.length) { row.push(field); rows.push(row); row = []; field = ""; }
    } else field += c;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  if (!rows.length) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  return rows.slice(1).filter((r) => r.some((v) => (v || "").trim())).map((r) => {
    const o = {}; headers.forEach((h, i) => { o[h] = (r[i] || "").trim(); }); return o;
  });
}

function LeadBulkUpload({ users, onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [defaultAssignee, setDefaultAssignee] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/leads/bulk-template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "leads-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setRows(parseCsv(text));
    setResult(null);
  };

  const submit = async () => {
    if (!rows.length) return toast.error("Choose a CSV with at least one lead");
    setBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-import", { rows, default_assignee: defaultAssignee || null });
      setResult(data);
      toast.success(`Imported ${data.created} lead(s)${data.skipped_duplicates ? `, ${data.skipped_duplicates} duplicate(s) skipped` : ""}`);
      if (!data.errors?.length) setTimeout(onDone, 1000);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-md w-full max-w-md p-6 shadow-lg max-h-[90vh] overflow-y-auto" data-testid="lead-bulk-modal">
        <h2 className="text-xl font-head font-extrabold mb-2">Bulk Upload Leads</h2>
        <p className="text-sm text-muted-foreground mb-4">Import old leads from a CSV. Add an <b>assigned_to_email</b> per row to route a lead to a specific user, or pick a default assignee below for any rows without one. Duplicates (matching phone/email/address) are skipped. Imported leads don't trigger automated emails.</p>
        <button onClick={downloadTemplate} data-testid="lead-bulk-template" className="w-full mb-3 px-4 py-2 rounded-md border border-border font-semibold text-sm flex items-center justify-center gap-2 hover:bg-muted">
          <Icons.Download size={16} /> Download CSV template
        </button>
        <label className="label-caps">CSV file</label>
        <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files[0])} data-testid="lead-bulk-file" className="w-full text-sm mb-3 mt-1" />
        {rows.length > 0 && <div className="text-xs text-emerald-600 mb-3">{rows.length} row(s) ready to import</div>}
        <label className="label-caps">Assign all rows to (default)</label>
        <select value={defaultAssignee} onChange={(e) => setDefaultAssignee(e.target.value)} data-testid="lead-bulk-assignee"
          className="w-full mt-1 mb-3 px-3 py-2 rounded-md border border-input bg-background text-sm">
          <option value="">Unassigned (or use assigned_to_email column)</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        {result && (
          <div className="text-xs bg-muted/40 rounded-md p-3 mb-3 space-y-1" data-testid="lead-bulk-result">
            <div className="text-emerald-600 font-semibold">{result.created} created · {result.skipped_duplicates} duplicates skipped</div>
            {(result.errors || []).map((e, i) => <div key={i} className="text-destructive">{e}</div>)}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border">Close</button>
          <button onClick={submit} disabled={busy} data-testid="lead-bulk-submit" className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold disabled:opacity-60">{busy ? "Importing…" : "Import"}</button>
        </div>
      </div>
    </div>
  );
}

const smsSegments = (t) => { const n = (t || "").length; return n === 0 ? 0 : Math.ceil(n / 160); };

function BulkMessageModal({ channel, count, leadIds, onClose, onSent }) {
  const isSms = channel === "sms";
  const [templates, setTemplates] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [saveName, setSaveName] = useState("");

  useEffect(() => {
    api.get(`/message-templates?channel=${channel}`).then((r) => setTemplates(r.data)).catch(() => {});
  }, [channel]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setBody(t.body || "");
    if (!isSms) setSubject(t.subject || "");
  };

  const saveTemplate = async () => {
    if (!saveName.trim()) return toast.error("Name the template first");
    if (!body.trim()) return toast.error("Nothing to save");
    if (!isSms && !subject.trim()) return toast.error("Email templates need a subject");
    try {
      const { data } = await api.post("/message-templates", { name: saveName, channel, subject, body });
      setTemplates((t) => [data, ...t]);
      setSaveName("");
      toast.success("Template saved for reuse");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const send = async () => {
    if (!body.trim()) return toast.error("Message cannot be empty");
    if (!isSms && !subject.trim()) return toast.error("Add a subject");
    setBusy(true);
    try {
      const url = isSms ? "/leads/bulk/send-sms" : "/leads/bulk/email";
      const payload = isSms ? { lead_ids: leadIds, body } : { lead_ids: leadIds, subject, body };
      const { data } = await api.post(url, payload);
      setResult(data);
      if (data.sent > 0) toast.success(`${data.sent} ${isSms ? "SMS" : "email"}(s) sent`);
      else toast.error("Nothing was sent — check the details below");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-md w-full max-w-lg p-6 shadow-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-message-modal">
        <div className="flex items-center gap-2 mb-1">
          <Icons.Send size={18} className="text-primary" />
          <h2 className="text-xl font-head font-extrabold">Bulk {isSms ? "SMS" : "Email"}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Sending to <b className="text-foreground">{count}</b> selected lead(s). Leads without a valid {isSms ? "phone" : "email"} are skipped.</p>

        {templates.length > 0 && (
          <div className="mb-4">
            <label className="label-caps">Use a saved template</label>
            <select onChange={(e) => applyTemplate(e.target.value)} defaultValue="" data-testid="bulk-template-select"
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm">
              <option value="">— None —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}

        {!isSms && (
          <div className="mb-3">
            <label className="label-caps">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="bulk-email-subject"
              placeholder="Your Grian Solar update" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
          </div>
        )}

        <label className="label-caps">Message</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="bulk-message-body"
          rows={isSms ? 4 : 7} placeholder={`Type your ${isSms ? "text" : "email"} message…`}
          className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background resize-y" />
        {isSms && (
          <div className={`text-xs mt-1 ${body.length > 160 ? "text-amber-600 font-medium" : "text-muted-foreground"}`} data-testid="bulk-sms-count">
            {body.length} characters · {smsSegments(body)} SMS{body.length > 160 ? ` — over 160 chars, Uniden sends as ${smsSegments(body)} messages` : ""}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mt-2">
          <span className="text-xs text-muted-foreground self-center mr-1">Insert:</span>
          {["{{first_name}}", "{{full_name}}"].map((v) => (
            <button key={v} type="button" onClick={() => setBody((b) => b + v)} data-testid={`bulk-var-${v.replace(/[{}]/g, "")}`}
              className="text-xs px-2 py-1 rounded bg-muted hover:bg-primary/10 hover:text-primary transition-colors font-mono">{v}</button>
          ))}
        </div>

        {/* Save as template */}
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border/50">
          <input value={saveName} onChange={(e) => setSaveName(e.target.value)} data-testid="bulk-save-template-name"
            placeholder="Save as template (name)" className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm" />
          <button onClick={saveTemplate} data-testid="bulk-save-template-btn"
            className="px-3 py-2 rounded-md border border-border text-sm font-semibold hover:bg-muted flex items-center gap-1.5"><Icons.Save size={14} /> Save</button>
        </div>

        {result && (
          <div className="text-xs bg-muted/40 rounded-md p-3 mt-4 space-y-1" data-testid="bulk-message-result">
            <div className="text-emerald-600 font-semibold">{result.sent} sent · {result.failed} failed · {result.skipped} skipped</div>
            {(result.errors || []).map((e, i) => <div key={i} className="text-destructive">{e}</div>)}
          </div>
        )}

        <div className="flex gap-2 justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border">{result ? "Done" : "Cancel"}</button>
          <button onClick={send} disabled={busy} data-testid="bulk-message-send"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold disabled:opacity-60 flex items-center gap-1.5">
            <Icons.Send size={15} /> {busy ? "Sending…" : `Send ${isSms ? "SMS" : "Email"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
